import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Heart, Wind, Eye, Hand, Ear, Check, ArrowRight } from "lucide-react";
import { LeadMagnet, useLeadMagnetState, LockedBadge } from "../components/LeadMagnet";

const breathingExercises = [
  {
    name: "Box Breathing",
    tagline: "When your mind is racing",
    color: "from-[#25a1c2] to-[#068fa2]",
    steps: ["Breathe IN for 4 seconds", "HOLD for 4 seconds", "Breathe OUT for 4 seconds", "HOLD for 4 seconds", "Repeat 4 times"],
    affirmation: "I GET TO slow down. My breath is my anchor.",
  },
  {
    name: "4-7-8 Calm Down",
    tagline: "When anxiety hits hard",
    color: "from-[#1E40AF] to-[#3B82F6]",
    steps: ["Breathe IN through your nose for 4 seconds", "HOLD your breath for 7 seconds", "Breathe OUT through your mouth for 8 seconds", "Repeat 3-4 times"],
    affirmation: "I GET TO release what I cannot control. I am safe right now.",
  },
  {
    name: "Belly Breathing",
    tagline: "When you feel tightness in your chest",
    color: "from-[#7C3AED] to-[#A855F7]",
    steps: ["Place one hand on your chest, one on your belly", "Breathe IN slowly — feel your belly rise (not your chest)", "Breathe OUT slowly — feel your belly fall", "Focus only on the rise and fall", "Continue for 2 minutes"],
    affirmation: "I GET TO be present in my body. My body knows how to heal.",
  },
  {
    name: "Counting Breath",
    tagline: "When thoughts won't stop",
    color: "from-[#B45309] to-[#D97706]",
    steps: ["Breathe in, think: ONE", "Breathe out, think: TWO", "Breathe in, think: THREE", "Continue to TEN", "If you lose count, start over — that's okay"],
    affirmation: "I GET TO quiet my mind. One breath at a time.",
  },
  {
    name: "Sigh It Out",
    tagline: "When you need it fast (30 seconds)",
    color: "from-[#DC2626] to-[#EF4444]",
    steps: ["Take a deep breath IN through your nose", "Let out a LONG, LOUD sigh through your mouth", "Let your shoulders drop completely", "Repeat 3 times — louder each time", "Shake your hands out after"],
    affirmation: "I GET TO let this go. I don't have to carry everything.",
  },
];

const grounding = {
  name: "5-4-3-2-1 Grounding",
  tagline: "When you feel disconnected or panicky",
  steps: [
    { sense: "SEE", count: 5, icon: <Eye className="w-6 h-6" />, prompt: "Name 5 things you can SEE right now", color: "bg-blue-50 border-blue-200 text-blue-800" },
    { sense: "TOUCH", count: 4, icon: <Hand className="w-6 h-6" />, prompt: "Name 4 things you can TOUCH right now", color: "bg-green-50 border-green-200 text-green-800" },
    { sense: "HEAR", count: 3, icon: <Ear className="w-6 h-6" />, prompt: "Name 3 things you can HEAR right now", color: "bg-purple-50 border-purple-200 text-purple-800" },
    { sense: "SMELL", count: 2, icon: <Wind className="w-6 h-6" />, prompt: "Name 2 things you can SMELL right now", color: "bg-amber-50 border-amber-200 text-amber-800" },
    { sense: "TASTE", count: 1, icon: <Heart className="w-6 h-6" />, prompt: "Name 1 thing you can TASTE right now", color: "bg-red-50 border-red-200 text-red-800" },
  ],
};

const emergencyAffirmations = [
  "This feeling is temporary. It will pass.",
  "I have survived every hard moment before this one.",
  "I GET TO feel this AND I GET TO let it move through me.",
  "I am not my anxiety. I am the person watching it.",
  "Right now, in this moment, I am safe.",
  "I don't have to figure everything out today.",
  "My feelings are valid. They are not facts.",
  "I GET TO take this one breath at a time.",
  "I am stronger than this moment.",
  "I GET TO ask for help. That is courage, not weakness.",
];

const BreathCard = ({ exercise, locked }) => (
  <div className="print:page-break-inside-avoid mb-8 relative">
    <div className={`bg-gradient-to-br ${exercise.color} rounded-2xl p-8 text-white ${locked ? "blur-sm select-none pointer-events-none" : ""}`}>
      <p className="text-white/60 text-xs uppercase tracking-widest font-bold mb-2">{exercise.tagline}</p>
      <h3 className="text-2xl font-extrabold mb-6">{exercise.name}</h3>
      <div className="space-y-3 mb-6">
        {exercise.steps.map((step, i) => (
          <div key={i} className="flex items-center gap-3">
            <span className="bg-white/20 text-white text-xs font-bold w-7 h-7 rounded-full flex items-center justify-center shrink-0">{i + 1}</span>
            <span className="text-white/90">{step}</span>
          </div>
        ))}
      </div>
      <div className="bg-white/10 rounded-xl p-4 mt-4">
        <p className="text-white/60 text-xs uppercase tracking-widest mb-1">Say to yourself:</p>
        <p className="text-white font-serif italic text-lg">"{exercise.affirmation}"</p>
      </div>
    </div>
    {locked && <LockedBadge label="Unlock all 5 breathing cards" />}
  </div>
);

export default function EmergencyCalmKit() {
  const { unlocked, markUnlocked } = useLeadMagnetState("emergency-calm-kit-unlocked");

  useEffect(() => {
    document.title = "Emergency Calm Kit — Printable Cards";
  }, []);

  return (
    <div className="bg-[#efeae7] min-h-screen print:bg-white">
      <div className="print:hidden fixed top-0 w-full z-50 bg-white/80 backdrop-blur-xl border-b border-[#CCFBF1]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-[#25a1c2] hover:text-[#068fa2] text-sm font-medium">
            &larr; Back to Shop
          </Link>
          <button onClick={() => window.print()} className="bg-[#25a1c2] hover:bg-[#068fa2] text-white rounded-full px-6 py-2.5 text-sm font-bold">
            Print / Save as PDF
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 pt-24 pb-16 print:pt-8 print:px-8">
        {/* Cover */}
        <div className="text-center mb-16 print:mb-10 print:page-break-after">
          <div className="w-20 h-20 bg-[#25a1c2] rounded-full flex items-center justify-center mx-auto mb-6">
            <Heart className="w-10 h-10 text-white" />
          </div>
          <p className="text-[#25a1c2] text-sm font-bold tracking-widest uppercase mb-3">A Mind Body Spirit HYGIENE™ Practice</p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#042F2E] tracking-tight mb-4">Emergency Calm Kit</h1>
          <p className="text-xl text-[#475569] mb-2">Anxiety HYGIENE™ — Real tools, for real women, in the messy middle.</p>
          <p className="text-[#25a1c2] font-medium">By Temple Franklin</p>
          <div className="mt-8 bg-[#F0F5F4] border border-[#CCFBF1] rounded-xl p-4 max-w-md mx-auto">
            <p className="text-[#068fa2] text-sm">
              Print these cards. Laminate them if you can. Carry them in your wallet, purse, or keep them by your bedside. When anxiety, panic, or overwhelm hits — pull out a card and breathe.
            </p>
          </div>
        </div>

        {/* 5 Breathing Exercise Cards */}
        <div className="mb-12">
          <h2 className="font-serif text-2xl md:text-3xl text-[#042F2E] text-center mb-8">5 Breathing Exercises</h2>
          {breathingExercises.map((exercise, i) => (
            <BreathCard key={i} exercise={exercise} locked={!unlocked && i !== 0} />
          ))}
        </div>

        {/* Lead magnet — appears after 1 free card, before the rest */}
        <LeadMagnet
          storageKey="emergency-calm-kit-unlocked"
          source="emergency-calm-kit-lead-magnet"
          formId="9350019"
          title="You deserve all 5 breathing tools."
          pitch="Box Breathing is just the start. Enter your email to unlock the other 4 breathing cards, the full 5-4-3-2-1 grounding exercise, and 10 emergency affirmation cards — all free."
          bullets={[
            "Unlock 4 more breathing cards (4-7-8, Belly, Counting, Sigh It Out)",
            "Full 5-4-3-2-1 grounding worksheet",
            "10 emergency affirmation cards you can cut out",
          ]}
          ctaLabel="✨ Unlock the Full Calm Kit"
          unlockedTitle="Full Calm Kit unlocked"
          unlockedCopy="Welcome. Print or save the PDF and carry it with you. You are safe."
          theme="teal"
          unlocked={unlocked}
          markUnlocked={markUnlocked}
        />

        {/* 5-4-3-2-1 Grounding */}
        <div className="mb-12 print:page-break-before relative">
          <div className={`bg-white rounded-3xl p-8 md:p-10 border border-[#CCFBF1] shadow-sm ${!unlocked ? "blur-sm select-none pointer-events-none print:blur-0" : ""}`}>
            <h2 className="font-serif text-2xl md:text-3xl text-[#042F2E] text-center mb-2">{grounding.name}</h2>
            <p className="text-[#475569] text-center mb-8">{grounding.tagline}</p>

            <div className="space-y-4">
              {grounding.steps.map((step, i) => (
                <div key={i} className={`${step.color} rounded-xl p-5 border flex items-start gap-4`}>
                  <div className="shrink-0 mt-1">{step.icon}</div>
                  <div>
                    <p className="font-bold text-lg">{step.count} things you can {step.sense}</p>
                    <p className="text-sm opacity-80 mt-1">{step.prompt}</p>
                    <div className="mt-3 space-y-2">
                      {Array.from({ length: step.count }).map((_, j) => (
                        <div key={j} className="h-6 border-b border-dashed border-current opacity-30" />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 bg-[#F0F5F4] rounded-xl p-5 text-center">
              <p className="font-serif text-[#25a1c2] italic text-lg">"I am here. I am present. I am safe. I GET TO come back to this moment."</p>
            </div>
          </div>
          {!unlocked && <LockedBadge label="Unlock grounding worksheet" />}
        </div>

        {/* Emergency Affirmation Cards */}
        <div className="mb-12 print:page-break-before relative">
          <h2 className="font-serif text-2xl md:text-3xl text-[#042F2E] text-center mb-2">Emergency Affirmation Cards</h2>
          <p className="text-[#475569] text-center mb-8">Cut these out. Keep them in your wallet. Pull one out when you need it most.</p>

          <div className={`grid grid-cols-2 gap-4 ${!unlocked ? "blur-sm select-none pointer-events-none print:blur-0" : ""}`}>
            {emergencyAffirmations.map((affirmation, i) => (
              <div
                key={i}
                className="bg-gradient-to-br from-[#068fa2] to-[#25a1c2] rounded-xl p-6 flex items-center justify-center text-center min-h-[140px]"
              >
                <p className="font-serif text-white italic leading-relaxed text-sm md:text-base">"{affirmation}"</p>
              </div>
            ))}
          </div>
          {!unlocked && <LockedBadge label="Unlock 10 affirmation cards" />}
        </div>

        {/* Quick Reference Card */}
        <div className="mb-12 print:page-break-before">
          <div className="bg-[#042F2E] rounded-3xl p-8 md:p-10 text-center">
            <h2 className="font-serif text-2xl text-white mb-6">Your Emergency Calm Sequence</h2>
            <p className="text-[#5EEAD4] text-sm mb-8">When panic or anxiety hits, follow these 4 steps in order:</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { step: "1", title: "STOP", desc: "Pause whatever you're doing. Put both feet on the floor." },
                { step: "2", title: "BREATHE", desc: "Pick any breathing card. Do 4 rounds." },
                { step: "3", title: "GROUND", desc: "Do the 5-4-3-2-1 exercise. Come back to NOW." },
                { step: "4", title: "AFFIRM", desc: "Pull an affirmation card. Read it 3 times out loud." },
              ].map((item) => (
                <div key={item.step} className="bg-white/10 rounded-xl p-5">
                  <span className="text-3xl font-extrabold text-[#5EEAD4]">{item.step}</span>
                  <h3 className="text-white font-bold mt-2 mb-1">{item.title}</h3>
                  <p className="text-white/60 text-xs">{item.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 bg-white/10 rounded-xl p-5">
              <p className="font-serif text-[#5EEAD4] italic text-lg">"I GET TO calm my nervous system. I GET TO come home to myself."</p>
              <p className="text-white/40 text-xs mt-3">mindbodyspirithygienetools.com — By Temple Franklin & the Divine Team</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center pt-8 mt-8 border-t border-[#CCFBF1]">
          <p className="text-[#25a1c2] font-medium text-sm">Emergency Calm Kit — Mind Body Spirit HYGIENE™ Tools</p>
          <p className="text-[#475569] text-xs mt-1">By Temple Franklin & the Divine Team</p>
          <p className="text-[#475569] text-xs mt-1">mindbodyspirithygienetools.com</p>
        </div>
      </div>
    </div>
  );
}
